import { v4 as uuidv4 } from 'uuid'

const retryIntervalMs = 5000
let retryTimer = null
let retryTimerLock = false

export function useMessageOrchestrator() {
  const {
    saveMessageTracking,
    updateMessageAcknowledgement,
    getUnacknowledgedMessages,
    incrementMessageRetryCount,
    markMessageAsFailed,
    hasProcessedMessage,
    markMessageAsProcessed,
    cleanupOldMessages,
    moveMessageToDLQ,
    getFailedMessages,
    getFailedMessagesCount,
    retryFailedMessageFromDLQ,
  } = usePosDatabase()

  const userStore = useUserStore()
  const socketStore = useSocketStore()
  const logger = useLogger()
  const nuxtApp = useNuxtApp()
  const { emitMessageFailed, emitMessageRetryFailed } = usePosEventBus()

  async function publishMessageToDevices(eventName, payload, targetDevices) {
    const uuid = uuidv4()
    const { deviceId } = useDeviceInfo()
    const { getInstanceId } = usePosDatabase()
    const instanceId = await getInstanceId()

    logger.info(`[Orchestrator] Publishing message ${uuid} from ${deviceId.value}:${instanceId}`)

    const enrichedPayload = {
      ...payload,
      sourceDeviceType: deviceId.value,
      sourceInstanceId: instanceId,
    }

    const messagePayload = {
      uuid,
      event: eventName,
      payload: enrichedPayload,
      timestamp: Date.now(),
    }

    try {
      await saveMessageTracking(uuid, eventName, payload, targetDevices)

      const channelName = `business-${userStore.activeBusinessId}-pos`

      if (!nuxtApp.$sc?.socket) {
        throw new Error('Socket not available')
      }

      await nuxtApp.$sc.socket.transmitPublish(channelName, messagePayload)

      logger.info(`[Orchestrator] Message ${uuid} published successfully`)
      return { success: true, uuid }
    } catch (error) {
      logger.error(`[Orchestrator] Failed to publish message ${uuid}:`, error)
      return { success: false, error }
    }
  }

  async function receiveMessageFromDevice(message) {
    const { uuid, event, payload } = message

    if (!uuid) {
      logger.warn('[Orchestrator] Received message without UUID, skipping deduplication')
      return { shouldProcess: true }
    }

    const alreadyProcessed = await hasProcessedMessage(uuid)

    if (alreadyProcessed) {
      logger.debug(`[Orchestrator] Message ${uuid} already processed, skipping`)
      return { shouldProcess: false }
    }

    const deviceId = await getDeviceId()
    await markMessageAsProcessed(uuid, event, deviceId)

    logger.info(`[Orchestrator] Processing new message ${uuid}`)
    return { shouldProcess: true }
  }

  async function sendAcknowledgement(messageUuid, deviceChannel) {
    const deviceId = await getDeviceId()

    const ackPayload = {
      type: 'ack',
      message_uuid: messageUuid,
      device_id: deviceId,
      timestamp: Date.now(),
    }

    try {
      const channelName = `business-${userStore.activeBusinessId}-${deviceChannel}`

      if (!nuxtApp.$sc?.socket) {
        throw new Error('Socket not available')
      }

      await nuxtApp.$sc.socket.transmitPublish(channelName, ackPayload)
      logger.debug(`[Orchestrator] ACK sent for message ${messageUuid}`)
    } catch (error) {
      logger.error(`[Orchestrator] Failed to send ACK for ${messageUuid}:`, error)
    }
  }

  async function receiveAcknowledgement(ackMessage) {
    const { message_uuid, device_id } = ackMessage

    logger.info(`[Orchestrator] Received ACK from ${device_id} for message ${message_uuid}`)

    try {
      await updateMessageAcknowledgement(message_uuid, device_id)
    } catch (error) {
      logger.error(`[Orchestrator] Failed to process ACK:`, error)
    }
  }

  async function retryUnacknowledgedMessages() {
    if (!socketStore.isConnected) {
      logger.debug('[Orchestrator] Socket offline, skipping retry')
      return
    }

    // Hard guard against re-broadcasting old DLQ messages under a different
    // user's identity. Without this, after logout + login as a different
    // business, a queued message originally sent under business A would
    // get republished onto `business-B-pos` (the channel name is built
    // live from userStore.activeBusinessId on every iteration) — wrong
    // business, wrong context. Skip until a new login establishes context.
    if (!userStore.isAuthenticated || !userStore.activeBusinessId) {
      logger.debug('[Orchestrator] Not authenticated or no active business, skipping retry')
      return
    }

    try {
      const unackedMessages = await getUnacknowledgedMessages(2)

      if (unackedMessages.length === 0) {
        return
      }

      logger.info(`[Orchestrator] Retrying ${unackedMessages.length} unacknowledged messages`)

      for (const msg of unackedMessages) {
        const unackedDevices = msg.targetDevices.filter(
          device => !msg.acknowledgements[device]?.received
        )

        if (unackedDevices.length === 0) {
          continue
        }

        const timeSinceCreation = Date.now() - msg.timestamp
        const retryDelay = Math.min(5000 * Math.pow(2, msg.retryCount), 30000)
        const jitter = Math.random() * 1000

        if (timeSinceCreation < retryDelay + jitter) {
          logger.debug(`[Orchestrator] Message ${msg.uuid} waiting for backoff (${Math.round((retryDelay + jitter - timeSinceCreation) / 1000)}s remaining)`)
          continue
        }

        logger.warn(`[Orchestrator] Retrying message ${msg.uuid} (attempt ${msg.retryCount + 1}) for devices:`, unackedDevices)

        const messagePayload = {
          uuid: msg.uuid,
          event: msg.eventName,
          payload: msg.payload,
          timestamp: msg.timestamp,
        }

        const channelName = `business-${userStore.activeBusinessId}-pos`

        if (!nuxtApp.$sc?.socket) {
          throw new Error('Socket not available')
        }

        await nuxtApp.$sc.socket.transmitPublish(channelName, messagePayload)

        await incrementMessageRetryCount(msg.uuid)

        if (msg.retryCount >= 2) {
          const failureReason = `Failed to deliver to: ${unackedDevices.join(', ')}`
          logger.error(`[Orchestrator] Message ${msg.uuid} failed after 3 attempts (2 retries), moving to DLQ`)
          await moveMessageToDLQ(msg.uuid, failureReason)

          emitMessageFailed(msg.uuid, msg.eventName, unackedDevices, failureReason)
        }
      }
    } catch (error) {
      logger.error('[Orchestrator] Error during retry:', error)
    }
  }

  async function retryFailedMessage(uuid) {
    logger.info(`[Orchestrator] Manually retrying failed message ${uuid}`)

    try {
      const msgData = await retryFailedMessageFromDLQ(uuid)

      await publishMessageToDevices(
        msgData.eventName,
        msgData.payload,
        msgData.targetDevices
      )

      logger.info(`[Orchestrator] Successfully retried message ${uuid}`)
      return { success: true }
    } catch (error) {
      logger.error(`[Orchestrator] Failed to retry message ${uuid}:`, error)
      return { success: false, error: error.message }
    }
  }

  function startRetryTimer() {
    if (retryTimer || retryTimerLock) {
      return
    }

    retryTimerLock = true
    try {
      logger.info('[Orchestrator] Starting retry timer')
      retryTimer = setInterval(async () => {
        try {
          await retryUnacknowledgedMessages()
        } catch (error) {
          logger.error('[Orchestrator] Error in retry timer:', error)
        }
      }, retryIntervalMs)
    } finally {
      retryTimerLock = false
    }
  }

  function stopRetryTimer() {
    if (retryTimer) {
      logger.info('[Orchestrator] Stopping retry timer')
      clearInterval(retryTimer)
      retryTimer = null
    }
  }

  // Auto-stop the retry timer when the user logs out. Without this,
  // the module-level interval keeps ticking after profile is cleared,
  // and the next iteration would try to publish queued messages with
  // userStore.activeBusinessId === null. Subscribe once per composable
  // instance — Pinia $subscribe handles dedup internally.
  if (typeof userStore.$subscribe === 'function') {
    userStore.$subscribe((mutation, state) => {
      if (!state.profile && retryTimer) {
        logger.info('[Orchestrator] User logged out, stopping retry timer')
        stopRetryTimer()
      }
    })
  }

  async function performCleanup() {
    try {
      await cleanupOldMessages(7)
      logger.info('[Orchestrator] Cleaned up old messages')
    } catch (error) {
      logger.error('[Orchestrator] Cleanup failed:', error)
    }
  }

  async function getDeviceType() {
    try {
      const { invoke } = await import('@tauri-apps/api/core')
      const deviceType = await invoke('get_device_type')
      return deviceType || 'pos'
    } catch {
      const { getPosSetting } = usePosDatabase()
      const isKds = await getPosSetting('isKdsMode')
      const isTablet = await getPosSetting('isTabletMode')

      if (isKds === '1') return 'kds'
      if (isTablet === '1') return 'tablet'
      return 'pos'
    }
  }

  // DEPRECATED: Use getDeviceType instead
  async function getDeviceId() {
    return getDeviceType()
  }

  return {
    publishMessageToDevices,
    receiveMessageFromDevice,
    sendAcknowledgement,
    receiveAcknowledgement,
    retryUnacknowledgedMessages,
    retryFailedMessage,
    startRetryTimer,
    stopRetryTimer,
    performCleanup,
    getDeviceType,
    getDeviceId, // DEPRECATED: Use getDeviceType instead
    getFailedMessages,
    getFailedMessagesCount,
  }
}
